import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingApproval, setPendingApproval] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await register(email, password, name, company);
      
      if (result.approved) {
        navigate('/dashboard');
      } else {
        setPendingApproval(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (pendingApproval) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom duration-500">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Shield className="h-12 w-12 text-primary" strokeWidth={1.5} />
            <div className="text-center">
              <h1 className="font-heading font-black text-3xl">FireCheck</h1>
              <p className="text-sm text-muted-foreground">Brandschutz-Abnahme</p>
            </div>
          </div>

          <Card className="border-border/50">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <h2 className="font-heading text-xl font-bold">Registrierung erfolgreich</h2>
                <p className="text-muted-foreground">
                  Ihre Registrierungsanfrage wurde eingereicht. Ein Administrator wird Ihr Konto prüfen und freischalten.
                </p>
                <p className="text-sm text-muted-foreground">
                  Sie werden per E-Mail benachrichtigt, sobald Ihr Konto aktiviert wurde.
                </p>
                <Link to="/login">
                  <Button variant="outline" className="mt-4">
                    Zurück zur Anmeldung
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom duration-500">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Shield className="h-12 w-12 text-primary" strokeWidth={1.5} />
          <div className="text-center">
            <h1 className="font-heading font-black text-3xl">FireCheck</h1>
            <p className="text-sm text-muted-foreground">Brandschutz-Abnahme</p>
          </div>
        </div>

        <Card className="border-border/50">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-2xl">Registrieren</CardTitle>
            <CardDescription>
              Erstellen Sie ein Konto. Ein Administrator wird Ihre Anfrage prüfen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-red-500 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Max Muster"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-12"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Firma</Label>
                <Input
                  id="company"
                  type="text"
                  placeholder="Muster Brandschutz AG"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="h-12"
                  data-testid="register-company-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-12"
                  data-testid="register-password-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 font-medium"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Registrieren...
                  </>
                ) : (
                  'Registrierung anfragen'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Bereits ein Konto?{' '}
                <Link 
                  to="/login" 
                  className="text-primary hover:underline font-medium"
                  data-testid="login-link"
                >
                  Anmelden
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
